import AnalyticsCrossProductInteractionClient, {
	type InteractionSessionTrackingOptions,
} from '@atlassiansox/analytics-cross-product-interaction-client/analytics-cross-product-interaction-client';
import { URL_QUERY_PARAM_INTERACTION_SESSION } from '@atlassiansox/analytics-cross-product-interaction-client/generate-url-with-params';
import { validProducts } from '@atlassiansox/analytics-cross-product-interaction-client/product-list';

import { perimeterType } from './analyticsWebTypes';
import { type InteractionSession } from './types';
import urlParamExtractor from './urlUtils';

const isCrossProductInteractionAttribute = (_: any, paramName: string) => {
	return paramName === URL_QUERY_PARAM_INTERACTION_SESSION;
};

type urlValidation = {
	result: boolean;
	url: string;
};

type CrossProductInteractionTrackingOptions = {
	historyReplaceFn?: any; // Confluence sets its own historyReplaceFn that works with its redirect logic
	perimeter?: string;
	product: string;
	sendEventCallback: Function;
};

/**
 * This class initialises the Analytics Cross Product Interaction Client, which keeps track of the
 * interactionSessionId we use to track cross-product user journeys.
 *
 * It parses URL query parameters for any previous interaction session attributes.
 * If none exist, the Analytics Cross Product Interaction Client will generate a new
 * interactionSessionId, and the class will expose a method for retrieving this from Session Storage
 *
 * @param sendEventCallback: Expects a callback function that can send an analytics event
 */
export default class CrossProductInteractionTracking {
	private analyticsCrossProductInteractionClient?: AnalyticsCrossProductInteractionClient;
	private isEnabled: boolean;
	private urlValidation: urlValidation;
	private sendEventCallback: Function;
	private validProduct: boolean = false;

	constructor(options: CrossProductInteractionTrackingOptions) {
		this.isEnabled = options.perimeter !== perimeterType.FEDRAMP_MODERATE;

		this.urlValidation = {
			result: true,
			url: '',
		};

		this.sendEventCallback = options.sendEventCallback;

		if (this.isEnabled) {
			let bridge: string | undefined;
			let interactionSessionId: string | undefined;
			let interactionSource: string | undefined;
			let interactionSessionOptions: InteractionSessionTrackingOptions | undefined;

			this.urlValidation.url = window.location.href;

			// Validate product
			//
			// Various UI components in the page might initialize their own AWC instances but some of those
			// instances might be associated with subordinate products that are of no interest to XPC tracking. We
			// want to avoid XPC meta analytics being fired against uninteresting products.
			//
			// Some products (e.g. Confluence) also have custom URL replace functions passed into AWC settings,
			// and we need to use those to ensure our query parameter is correctly processed.
			//
			// TODO - this is currently the best way we can try and guarantee that the most relevant product's AWC
			// is passing in the options. Down the line we ideally want the relevant products themselves to opt-in
			// to initialising cross product tracking via a flag in their AWC init options.
			this.validProduct = validProducts.includes(options.product);

			if (this.validProduct) {
				// Only parse URL parameters if we are in a valid product
				const crossProductUrlPramater = urlParamExtractor(
					isCrossProductInteractionAttribute,
					options.historyReplaceFn,
				);
				const interactionSessionBlob =
					URL_QUERY_PARAM_INTERACTION_SESSION in crossProductUrlPramater
						? crossProductUrlPramater[URL_QUERY_PARAM_INTERACTION_SESSION]
						: null;

				if (interactionSessionBlob) {
					try {
						const interactionSession = JSON.parse(atob(interactionSessionBlob));

						if (interactionSession.bridge && interactionSession.id && interactionSession.source) {
							// All 3 fields must be present from a valid query param
							bridge = interactionSession.bridge;
							interactionSessionId = interactionSession.id;
							interactionSource = interactionSession.source;
						} else {
							this.urlValidation.result = false;
						}
					} catch {
						// Gracefully fail through if parameter has been corrupted
						this.urlValidation.result = false;
					}
				}

				interactionSessionOptions = {
					analyticsEventCallback: this.sendEventCallback,
					persistedSessionId: interactionSessionId,
				};
			}

			// Initialise interaction session client with parsed values if they exist
			this.analyticsCrossProductInteractionClient = new AnalyticsCrossProductInteractionClient(
				interactionSessionOptions,
				bridge,
				interactionSource,
			);
		}
	}

	getInteractionSession(): InteractionSession {
		if (this.isEnabled && this.analyticsCrossProductInteractionClient) {
			return {
				bridge: this.analyticsCrossProductInteractionClient.getBridge(),
				id: this.analyticsCrossProductInteractionClient.getCurrentInteractionSessionId(),
				prevId: this.analyticsCrossProductInteractionClient.getPreviousInteractionSessionId(),
				source: this.analyticsCrossProductInteractionClient.getInteractionSource(),
			};
		} else {
			return {
				bridge: null,
				id: null,
				prevId: null,
				source: null,
			};
		}
	}

	/**
	 * Validates the cross product interaction session parameters parsed from the URL (if present)
	 * If the Base64-encoded URL does not decode into a valid JSON object
	 *
	 * This method must be called out of the constructor, as the sendEventCallback we expect here
	 * depends on crossProductInteractionTracking.getInteractionSession() so the class must be initialised
	 */
	validateInteractionSession(): void {
		if (!this.urlValidation.result) {
			this.sendEventCallback({
				action: 'corrupted',
				actionSubject: 'interactionSession',
				attributes: {
					url: this.urlValidation.url,
				},
				source: 'interactionComponent',
				tags: ['navigationExperiences'], // InteractionSession events will be registered under this platform product
			});
		}
	}
}
